'use client';

import { motion, MotionConfig } from 'motion/react';

import { ResponsiveImage } from '@natu/ui/ResponsiveImage';
import { cn } from '@natu/utils/cn';

type AvatarImage = {
  id: number;
  src?: string | null;
  alt?: string | null;
  title?: string | null;
};

interface RoundingAvatarProps {
  size: 'small' | 'big';
  avatar: AvatarImage;
  className?: string;
}

const sizeClasses = {
  small: 'w-12 h-12',
  big: 'w-16 h-16',
};

export const RoundedAvatar = ({ size, avatar, className }: RoundingAvatarProps) => (
  <div
    key={avatar.id}
    className={cn(className, 'relative overflow-hidden rounded-full', sizeClasses[size])}
  >
    <ResponsiveImage
      width={size === 'small' ? 48 : 64}
      height={size === 'small' ? 48 : 64}
      src={avatar.src as string}
      alt={avatar.alt || avatar.title || 'Naturaily person'}
    />
  </div>
);

interface FloatingAvatarsProps {
  avatars?: AvatarImage[];
}

export const FloatingAvatars = ({ avatars }: FloatingAvatarsProps) => (
  <MotionConfig transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}>
    <div className="absolute inset-0 overflow-hidden">
      {avatars?.[0] && (
        <motion.div
          className="absolute left-[69px] top-[51px] lg:left-[391px] lg:top-20"
          animate={{ y: [0, 15, 0] }}
        >
          <div className="round bg-primary-300 absolute -left-7 top-5 z-0 h-9 w-9 rounded-bl-full lg:left-8" />
          <RoundedAvatar avatar={avatars[0]} size="small" />
        </motion.div>
      )}
      {avatars?.[1] && (
        <motion.div
          className="absolute right-[77px] top-[76px] lg:right-[106px] lg:top-[134px]"
          animate={{ y: [0, 10, 0] }}
        >
          <RoundedAvatar avatar={avatars[1]} size="small" />
        </motion.div>
      )}

      <motion.div
        className="round bg-naturaily-butterscotch absolute -right-3 bottom-[216px] h-6 w-6 rounded-full lg:bottom-[50%] lg:right-[78px]"
        animate={{ y: [0, 30, 0] }}
      />

      {avatars?.[2] && (
        <motion.div
          className="absolute bottom-[50%] left-[78px] hidden lg:block"
          animate={{ y: [0, 10, 0] }}
        >
          <RoundedAvatar avatar={avatars[2]} size="small" />
          <div className="bg-naturaily-airborne round absolute -left-2 -top-4 h-6 w-6 rounded-tl-full" />
        </motion.div>
      )}

      {avatars?.[3] && (
        <motion.div
          className="absolute bottom-[180px] right-[80px] hidden lg:block"
          animate={{ y: [0, 25, 0] }}
        >
          <RoundedAvatar avatar={avatars[3]} size="big" />
          <div className="round absolute left-[52px] top-0 h-6 w-6 rounded-tl-full bg-white" />
        </motion.div>
      )}

      <motion.div
        className="round absolute bottom-[94px] right-[40px] h-6 w-6 rounded-tl-full bg-white lg:hidden"
        animate={{ y: [0, 10, 0] }}
      />

      {avatars?.[4] && (
        <motion.div
          className="absolute bottom-[65px] left-[150px] lg:bottom-[140px] lg:left-[264px]"
          animate={{ y: [0, 20, 0] }}
        >
          <RoundedAvatar avatar={avatars[4]} size="big" />
        </motion.div>
      )}
    </div>
  </MotionConfig>
);
